/**
 * Original chiptune scores. Each track is a grid of eighth notes; '.' is a rest and
 * '-' holds the previous note. All melodies here were written for this project.
 */

export interface Song {
  bpm: number;
  /** Chord per bar, used to generate the bass line. */
  chords: string[];
  /** Lead melody, one string of 8 eighth-note tokens per bar. */
  lead: string[];
  /** Optional counter-melody / arpeggio layer. */
  harmony?: string[];
  /** 8-step drum pattern: K = thump, s = snap, h = tick, . = silence. */
  drums: string;
  /** Bass octave. */
  bassOct: number;
  loop: boolean;
  /** Lead waveform. */
  wave: OscillatorType;
}

/** Overworld: bright, marching, pentatonic-flavoured. */
export const OVERWORLD: Song = {
  bpm: 150,
  bassOct: 2,
  wave: 'square',
  loop: true,
  chords: [
    'C',
    'C',
    'F',
    'C',
    'G',
    'F',
    'C',
    'G',
    'Am',
    'Am',
    'F',
    'G',
    'C',
    'Am',
    'G',
    'C',
  ],
  lead: [
    'C5 . E5 G5 . A5 G5 E5',
    'F5 . E5 D5 . C5 . .',
    'D5 . F5 A5 . C6 A5 F5',
    'G5 . E5 C5 . . . .',
    'E5 . E5 F5 . G5 . G5',
    'A5 . G5 F5 . E5 . D5',
    'C5 . D5 E5 . F5 G5 A5',
    'G5 . . . . . . .',
    'A4 . C5 E5 . D5 C5 A4',
    'B4 . D5 F5 . E5 D5 B4',
    'C5 . E5 G5 . A5 G5 E5',
    'F5 . D5 . B4 . . .',
    'C5 . G5 . E5 . C5 .',
    'A4 . E5 . C5 . A4 .',
    'B4 . D5 . G5 . F5 .',
    'E5 . C5 . . . . .',
  ],
  harmony: [
    'G4 . C5 . . E5 . C5',
    'A4 . G4 . . G4 . .',
    'F4 . A4 . . F5 . A4',
    'B4 . G4 . . . . .',
    'C5 . C5 . . B4 . B4',
    'F4 . E4 . . C5 . A4',
    'E4 . F4 . . A4 . C5',
    'B4 . . . . . . .',
    'E4 . A4 . . F4 . E4',
    'F4 . B4 . . G4 . F4',
    'G4 . C5 . . E5 . C5',
    'A4 . F4 . . D4 . .',
    'E4 . E5 . C5 . E4 .',
    'C5 . A4 . E4 . C5 .',
    'D5 . B4 . D4 . B4 .',
    'C5 . E4 . . . . .',
  ],
  drums: 'K.h.s.hh',
};

/** Underground: sparse, low, uneasy. */
export const CAVERN: Song = {
  bpm: 128,
  bassOct: 1,
  wave: 'triangle',
  loop: true,
  chords: ['Am', 'Am', 'Dm', 'Dm', 'F', 'F', 'E', 'E'],
  lead: [
    'A3 . . A3 . C4 . .',
    'E4 . D4 . C4 . A3 .',
    'D4 . . D4 . F4 . .',
    'A4 . G4 . F4 . D4 .',
    'F4 . . A4 . C5 . .',
    'A4 . F4 . E4 . C4 .',
    'E4 . . G#4 . B4 . .',
    'E4 . . . . . . .',
  ],
  harmony: [
    '. . E3 . . . . E3',
    '. . . . A3 . . .',
    '. . A3 . . . . A3',
    '. . . . D4 . . .',
    '. . C4 . . . . C4',
    '. . . . F3 . . .',
    '. . B3 . . . . B3',
    '. . . . E3 . . .',
  ],
  drums: 'K...s...',
};

/** Prism power: fast and frantic. */
export const PRISM: Song = {
  bpm: 200,
  bassOct: 2,
  wave: 'square',
  loop: true,
  chords: ['C', 'F', 'G', 'C'],
  lead: [
    'C5 E5 G5 E5 C5 E5 G5 A5',
    'F5 A5 C6 A5 F5 A5 C6 D6',
    'G5 B5 D6 B5 G5 B5 D6 E6',
    'C6 G5 E5 C5 G4 E4 C4 .',
  ],
  drums: 'KhshKhsh',
};

/** One-shot fanfare when a level is cleared. */
export const CLEAR: Song = {
  bpm: 160,
  bassOct: 2,
  wave: 'square',
  loop: false,
  chords: ['C', 'G', 'C', 'C'],
  lead: [
    'C5 E5 G5 C6 . E6 . .',
    'D6 . B5 . G5 . D5 .',
    'E5 G5 C6 E6 . G6 . .',
    'C6 . . . . . . .',
  ],
  drums: 'K.s.K.ss',
};

/** One-shot sting on game over. */
export const GAMEOVER: Song = {
  bpm: 120,
  bassOct: 2,
  wave: 'triangle',
  loop: false,
  chords: ['Am', 'Dm', 'E', 'Am'],
  lead: ['A4 . G4 . F4 . E4 .', 'D4 . . C4 . . B3 .', 'E4 . . . D4 . . .', 'A3 . . . . . . .'],
  drums: '........',
};

export const SONGS = { OVERWORLD, CAVERN, PRISM, CLEAR, GAMEOVER } as const;
export type SongName = keyof typeof SONGS;
